import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Storefront, MapPin, Plus, Users, Gear, ArrowRight, CalendarDots, GameController, Check, Trophy } from '@phosphor-icons/react';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShopsPage() {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [myShops, setMyShops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchShops();
    fetchMyShops();
  }, []);

  const fetchShops = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/shops`, { withCredentials: true });
      setShops(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching shops:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyShops = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/shop-portal/my-shops`, { withCredentials: true });
      setMyShops(Array.isArray(data) ? data : []);
    } catch {}
  };

  return (
    <div className="app-container" data-testid="shops-page">
      <header className="pt-5 px-6 pb-4 flex items-center justify-between">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            <Storefront size={24} weight="duotone" className="inline mr-2 text-[#00F0FF]" />
            Game Shops
          </h1>
          <p className="text-[#A1A1AA] text-sm">Find and join local game stores</p>
        </motion.div>
        <button onClick={() => navigate('/shop-leaderboard')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F59E0B]/10 text-[#F59E0B] text-xs font-medium hover:bg-[#F59E0B]/20 transition-all" data-testid="leaderboard-btn">
          <Trophy size={14} weight="duotone" /> Rankings
        </button>
      </header>

      <div className="px-6 pb-32">
        {/* My Shops Portal Banner */}
        {myShops.length > 0 && (
          <div className="mb-4 space-y-2" data-testid="my-shops-banner">
            {myShops.slice(0, 3).map(s => (
              <motion.div key={s.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                onClick={() => navigate(`/shop-portal/${s.id}`)}
                className="flex items-center justify-between p-3 rounded-xl bg-[#00F0FF]/5 border border-[#00F0FF]/20 cursor-pointer hover:bg-[#00F0FF]/10 transition-all">
                <div className="flex items-center gap-3">
                  <Gear size={18} className="text-[#00F0FF]" />
                  <div>
                    <p className="text-white text-sm font-medium">{s.name}</p>
                    <p className="text-[10px] text-[#00F0FF] uppercase tracking-wider">{s.role} portal</p>
                  </div>
                </div>
                <ArrowRight size={16} className="text-[#00F0FF]" />
              </motion.div>
            ))}
          </div>
        )}

        {/* Register Shop CTA removed — players cannot become shop owners
            from inside their player account. Shops must sign up at
            /register-shop with a separate account (see strict-separation
            launch redesign). The empty-state below directs them there. */}

        {/* Shops List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 rounded-2xl bg-[#0F0F16] animate-pulse"></div>
            ))}
          </div>
        ) : shops.length > 0 ? (
          <div className="space-y-3" data-testid="shops-list">
            {shops.map((shop, index) => (
              <motion.div
                key={shop.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => navigate(`/shops/${shop.id}`)}
                className="p-4 rounded-2xl bg-[#0F0F16] border border-white/5 cursor-pointer hover:border-[#00F0FF]/30 transition-all"
                data-testid={`shop-${shop.id}`}
              >
                <div className="flex gap-4">
                  {shop.image ? (
                    <img src={shop.image} alt={shop.name} className="w-20 h-20 rounded-xl object-cover" />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-[#9D4CDD]/20 to-[#00F0FF]/20 flex items-center justify-center">
                      <Storefront size={32} weight="duotone" className="text-[#00F0FF]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-semibold truncate">{shop.name}</h3>
                      {shop.verified && <Check size={14} className="text-[#00F0FF] flex-shrink-0" />}
                    </div>
                    <p className="text-[#71717A] text-sm flex items-center gap-1 mt-1">
                      <MapPin size={14} weight="duotone" className="text-[#9D4CDD]" />
                      {shop.location}
                    </p>
                    <p className="text-[#A1A1AA] text-xs mt-2 line-clamp-2">{shop.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      <span className="text-[#39FF14] flex items-center gap-1"><Users size={12} weight="duotone" /> {shop.member_count || 0}</span>
                      {shop.open_play && <span className="text-[#9D4CDD] flex items-center gap-1"><GameController size={12} /> Open Play</span>}
                      {shop.events_enabled && <span className="text-[#00F0FF] flex items-center gap-1"><CalendarDots size={12} /> Events</span>}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-[#71717A]" data-testid="shops-empty-state">
            <Storefront size={48} weight="duotone" className="mx-auto mb-4 opacity-50" />
            <p>No game shops in your area yet</p>
            <p className="text-sm mt-1">Check back soon — new shops join every week.</p>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
